import { useLocation, useNavigate } from "react-router-dom"

import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarTrigger,
} from "@/ui/sidebar"

import sidebarItems from "./sidebar-items.json"
import { authApi } from "@/features/auth/api/auth.api"
import type { GetUserModuleConfigResponse, UserModuleConfigItem } from "@/features/auth/types"
import { STORAGE_KEYS } from "@/shared/constants"
import { MaterialIcon } from '@/ui/material-icon'
import styles from './Sidebar.module.scss'
type SidebarItem = {
  id: string
  label: string
  icon?: string
  path?: string
  position?: "main" | "footer"
  group?: string
  order?: number
  features?: SidebarItem[]
  children?: SidebarItem[]
}

const iconAliases: Record<string, string> = {
  layoutdashboard: "dashboard",
  wrench: "build",
  palette: "palette",
  headphones: "headset",
  settings: "settings",
}

const mapExternalIcon = (icon?: string): SidebarItem['icon'] | undefined => {
  if (!icon) return undefined
  const normalizedIcon = icon.trim()
  const alias = iconAliases[normalizedIcon.toLowerCase()]
  return alias ?? normalizedIcon
}

/**
 * Backend RBAC config is based on legacy paths.
 * Translate it to the new manifest-driven URLs.
 */
const mapLegacySidebarPath = (path?: string): string | undefined => {
  if (!path) return undefined

  if (path === '/role') return '/roles'
  if (path === '/users') return '/users'

  // Defensive handling if backend still sends tab-based URLs.
  if (path.startsWith('/users?')) {
    if (path.includes('tab=deleted')) return '/users/deleted'
    return '/users/active'
  }
  if (path.startsWith('/role?')) {
    if (path.includes('tab=deleted')) return '/roles/deleted'
    return '/roles/active'
  }

  return path
}

const sortSidebarItems = (items: SidebarItem[]): SidebarItem[] => {
  return [...items]
    .map((item) => ({
      ...item,
      features: item.features ? sortSidebarItems(item.features) : undefined,
      children: item.children ? sortSidebarItems(item.children) : undefined,
    }))
    .sort((a, b) => {
      const aOrder = a.order ?? Number.MAX_SAFE_INTEGER
      const bOrder = b.order ?? Number.MAX_SAFE_INTEGER
      return aOrder - bOrder
    })
}

const normalizeMenuItem = (
  item: UserModuleConfigItem,
  parentGroup?: string
): SidebarItem => {
  const id = item.id ?? item.path ?? item.label
  const idLower = String(id ?? '').toLowerCase()
  const labelLower = String(item.label ?? '').toLowerCase()

  const resolvedPath = mapLegacySidebarPath(item.path)
  const externalIcon = mapExternalIcon(item.icon)

  const base: SidebarItem = {
    id,
    label: item.label,
    path: resolvedPath,
    icon:
      labelLower === 'appearance' || resolvedPath === '/appearance'
        ? 'palette'
        : externalIcon,
    position: item.position,
    group: item.group ?? parentGroup,
    order: item.order,
  }

  // Some backends may not return `position`; enforce footer for these entries.
  // This keeps `Support` and `Settings` pinned to the sidebar footer.
  const currentPath = base.path
  if (!base.position) {
    const isSupport =
      idLower === 'support' ||
      labelLower === 'support' ||
      currentPath === '/support'

    const isSettings =
      idLower === 'settings' ||
      labelLower === 'settings' ||
      currentPath === '/settings'

    if (isSupport) base.position = 'footer'
    if (isSettings) base.position = 'footer'

    // If backend doesn't provide `path`, default so navigation + active state still work.
    if (!base.path) {
      if (isSupport) base.path = '/support'
      if (isSettings) base.path = '/settings'
    }
  }
  const childrenSource = (item as any).features ?? (item as any).children ?? []
  if (childrenSource.length > 0) {
    return {
      ...base,
      features: (childrenSource as UserModuleConfigItem[]).map((child) =>
        normalizeMenuItem(child, item.group ?? parentGroup)
      ),
    }
  }

  return base
}

const AppSidebar = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const pathname = location.pathname
  const [expanded, setExpanded] = useState<string[]>([])
  const [manuallyCollapsed, setManuallyCollapsed] = useState<string[]>([])

  const storedUser = localStorage.getItem(STORAGE_KEYS.USER)
  const userId = storedUser ? (JSON.parse(storedUser) as { id?: string }).id : undefined

  const {
    data: moduleConfigResponse,
    isLoading: isModuleConfigLoading,
    isError: moduleConfigError,
  } = useQuery<GetUserModuleConfigResponse, Error>({
    queryKey: ['rbac', 'module-config', userId],
    queryFn: () => authApi.getUserModuleConfig(userId),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  })

  const menuItems = useMemo<SidebarItem[]>(() => {
    const fetchedItems = moduleConfigResponse?.data ?? []
    if (fetchedItems.length > 0) {
      return sortSidebarItems(fetchedItems.map((item) => normalizeMenuItem(item)))
    }
    return sidebarItems as SidebarItem[]
  }, [moduleConfigResponse])

  const mainItems = useMemo(
    () => menuItems.filter((item) => item.position !== 'footer'),
    [menuItems]
  )

  const groupedMainItems = useMemo(() => {
    return mainItems.reduce<Record<string, SidebarItem[]>>((acc, item) => {
      const group = item.group ?? 'Admin'
      if (!acc[group]) acc[group] = []
      acc[group].push(item)
      return acc
    }, {})
  }, [mainItems])

  const footerItems = useMemo(
    () => menuItems.filter((item) => item.position === 'footer'),
    [menuItems]
  )


  const isSelfActive = (item: SidebarItem) => {
    if (!item.path) return false
    return pathname === item.path || pathname.startsWith(`${item.path}/`)
  }

  const toggleExpand = (id: string) => {
    const isExpandedByUser = expanded.includes(id)
    const isManuallyCollapsed = manuallyCollapsed.includes(id)

    if (isExpandedByUser) {
      setExpanded((current) => current.filter((entry) => entry !== id))
      setManuallyCollapsed((current) =>
        current.includes(id) ? current : [...current, id]
      )
      return
    }

    if (isManuallyCollapsed) {
      setManuallyCollapsed((current) => current.filter((entry) => entry !== id))
      return
    }

    setExpanded((current) => [...current, id])
    setManuallyCollapsed((current) => current.filter((entry) => entry !== id))
  }

  const hasActiveDescendant = (item: SidebarItem): boolean => {
    const children = item.features ?? item.children ?? []
    if (children.length === 0) return false

    return children.some((child) => {
      const childIsActive =
        Boolean(child.path) &&
        (pathname === child.path || pathname.startsWith(`${child.path}/`))
      return childIsActive || hasActiveDescendant(child)
    })
  }

  const renderMenuItems = (
    items: SidebarItem[],
    depth = 0
  ): React.ReactNode => {
    return items.map((item, index) => {
      const iconName = item.icon
      const children = item.features ?? item.children ?? []
      const descendantActive = hasActiveDescendant(item)
      const active =
        children.length > 0
          ? isSelfActive(item) && !descendantActive
          : isSelfActive(item)
      const open =
        !manuallyCollapsed.includes(item.id) &&
        (expanded.includes(item.id) || descendantActive)
      const dotIndicator = (
        <span className={styles.dotIndicator} />
      )
      const leftIcon = depth >= 2 ? dotIndicator : null

      if (children.length > 0) {
        return (
          <SidebarMenuItem
            key={`${item.id}-${depth}-${index}`}
            data-has-submenu="true"
            data-depth={depth}
            className={`${styles.menuItem} ${depth === 1 ? styles.depthOneBorder : ""
              }`}
          >
            <SidebarMenuButton
              onClick={() => toggleExpand(item.id)}
              isActive={active}
              aria-expanded={open}
              data-descendant-active={descendantActive ? "true" : undefined}
              className={styles.menuButtonJustify}
            >
              <span className={styles.rowContent}>
                {leftIcon}
                <span className={styles.itemIcon}>
                  {iconName && (
                    <MaterialIcon
                      name={iconName}
                      size={24}
                      className={
                        descendantActive ? styles.parentIconActive : undefined
                      }
                    />
                  )}
                </span>
                <span className={styles.itemLabel}>{item.label}</span>
              </span>
              <MaterialIcon
                name={open ? "expand_more" : "chevron_right"}
                size={24}
                className={styles.chevronIcon}
              />
            </SidebarMenuButton>

            {open && (
              <SidebarMenuSub className={styles.subMenuNoBorder}>
                {renderMenuItems(children, depth + 1)}
              </SidebarMenuSub>
            )}
          </SidebarMenuItem>
        )
      }

      return (
        <SidebarMenuItem
          key={`${item.id}-${depth}-${index}`}
          data-has-submenu="false"
          data-depth={depth}
          className={`${styles.menuItem} ${depth === 1 ? styles.depthOneBorder : ""
            }`}
        >
          <SidebarMenuButton
            onClick={() => {
              if (item.path) navigate(item.path)
            }}
            isActive={active}
            className={depth > 0 ? styles.leafIndent : undefined}
          >
            <span className={styles.rowContent}>
              {leftIcon}
              <span className={styles.itemIcon}>
                {iconName && <MaterialIcon name={iconName} size={24} />}
              </span>
              <span className={styles.itemLabel}>{item.label}</span>
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )
    })
  }

  return (
    <Sidebar collapsible="icon" className={styles.sidebar}>
      <SidebarHeader className={styles.sidebarHeader}>
        <SidebarTrigger className={styles.sidebarToggle} />
      </SidebarHeader>

      <SidebarContent>
        {isModuleConfigLoading && (
          <div className={styles.loadingText}>Loading sidebar...</div>
        )}
        {moduleConfigError && (
          <div className={styles.errorText}>
            Failed to load sidebar menu; using default menu.
          </div>
        )}
        {Object.entries(groupedMainItems).map(([group, items], idx) => (
          <div
            key={group}
            className={`${styles.groupSection} ${idx === 0 ? styles.firstGroupSection : ""
              }`}
          >
            <div className={styles.groupLabel}>{group}</div>
            <SidebarMenu>{renderMenuItems(items)}</SidebarMenu>
          </div>
        ))}
      </SidebarContent>

      <SidebarFooter className={styles.SidebarFooter}>
        <SidebarMenu>
          {footerItems.map((item) => {
            const iconName = item.icon
            const active = isSelfActive(item)
            return (
              <SidebarMenuItem
                key={item.id}
                data-has-submenu="false"
                data-depth={0}
                className={styles.menuItem}
              >
                <SidebarMenuButton
                  onClick={() => item.path && navigate(item.path)}
                  isActive={active}
                >
                  <span className={styles.rowContent}>
                    <span className={styles.itemIcon}>
                      {iconName && <MaterialIcon name={iconName} size={24} />}
                    </span>
                    <span className={styles.itemLabel}>{item.label}</span>
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
        <div className={styles.footerVersion}>Version 1.02.09</div>
      </SidebarFooter>
    </Sidebar>
  )
}

export default AppSidebar
